import { z } from "zod";

import { allCountryCodes, matchAndClubCountryCodes } from "./countries";
import { deDivisionsList, frDivisionsList, itDivisionsList, playerFFTranking, playerGenders, playerLaterality, playerMatchAvailabilities, playerPlayingStyles, playerPreferredSurface, rankingTypes } from "./enums";
import { phoneNumberSchema } from "./user";
import { countryMsg, dateMsg, minLengthMsg, priceMsg, requiredMsg } from "./zod-error-msg";

export const playerInformationsSchema = z.object({
  firstName: z.string().min(2, { message: minLengthMsg }),
  lastName: z.string().min(2, { message: minLengthMsg }),
  gender: z.enum(playerGenders, { message: requiredMsg }),
  nationalityCode: z.enum(allCountryCodes, { message: countryMsg }),
  description: z.string().max(750, { message: "common:zod.description" }).optional(),
  birthdate: z.string({ message: requiredMsg }).date(dateMsg),
  imgUrl: z.string().url().optional(),
});
export type PlayerInformationsSchema = z.infer<typeof playerInformationsSchema>;

export const playerInformationsUpdateSchema = playerInformationsSchema.and(z.object({ userId: z.string().uuid() }));
export type PlayerInformationsUpdateSchema = z.infer<typeof playerInformationsUpdateSchema>;

export const onboardingPlayerSchema = z.object({
  firstName: z.string().min(2, { message: minLengthMsg }),
  lastName: z.string().min(2, { message: minLengthMsg }),
  phone: phoneNumberSchema,
  birthdate: z.string({ message: requiredMsg }).date(dateMsg),
  gender: z.enum(playerGenders, { message: requiredMsg }),
  nationalityCode: z.enum(allCountryCodes, { message: countryMsg }),
});

export const bestRankingYearSchema = z.coerce.number({
  coerce: true,
}).transform((data, ctx) => {
  const fullYear = new Date().getFullYear();

  if (data < 1950 || data > fullYear) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "common:zod.dateFromTo",
    });
  }

  return data;
});

export const updatePlayerFFTRankingSchema = z.object({
  currentFFTRanking: z.enum(playerFFTranking).nullable(),
  currentFFTRankingNumber: z.coerce.number().nullable(),
  bestFFTRanking: z.enum(playerFFTranking).nullable(),
  bestFFTRankingYear: bestRankingYearSchema.nullable(),
  bestFFTRankingNumber: z.coerce.number().nullable(),
}).superRefine((data, ctx) => {
  if (data.bestFFTRanking === "T100" && !data.bestFFTRankingNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMsg,
      path: ["bestFFTRankingNumber"],
    });
  }

  if (data.currentFFTRanking === "T100" && !data.currentFFTRankingNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredMsg,
      path: ["currentFFTRankingNumber"],
    });
  }
});
export type UpdatePlayerFFTRankingSchema = z.infer<typeof updatePlayerFFTRankingSchema>;

// regex to match utr format (##.##)
export const UTRRegex = /^\d{1,2}\.\d{2}$/;

export const updatePlayerUTRRankingSchema = z.object({
  currentUTRRanking: z.string().regex(UTRRegex).nullable(),
  bestUTRRanking: z.string().regex(UTRRegex).nullable(),
  bestUTRRankingYear: bestRankingYearSchema.nullable(),
});
export type UpdatePlayerUTRRankingSchema = z.infer<typeof updatePlayerUTRRankingSchema>;

// regex to match atp/wta format (Between 1 and 2000)
export const ATP_WTA_ITF_Regex = /^([1-9]|[1-9]\d|[1-9]\d\d|1\d\d\d|2000)$/;

export const updatePlayerATPWTARankingSchema = z.object({
  currentATPWTARanking: z.string().regex(ATP_WTA_ITF_Regex).nullable(),
  bestATPWTARanking: z.string().regex(ATP_WTA_ITF_Regex).nullable(),
  bestATPWTARankingYear: bestRankingYearSchema.nullable(),
  lastATPWTARanking: z.string().regex(ATP_WTA_ITF_Regex).nullable(),
  lastATPWTARankingYear: bestRankingYearSchema.nullable(),
});
export type UpdatePlayerATPWTARankingSchema = z.infer<typeof updatePlayerATPWTARankingSchema>;

export const updatePlayerITFRankingSchema = z.object({
  currentITFRanking: z.string().regex(ATP_WTA_ITF_Regex).nullable(),
  bestITFRanking: z.string().regex(ATP_WTA_ITF_Regex).nullable(),
  bestITFRankingYear: bestRankingYearSchema.nullable(),
  lastITFRanking: z.string().regex(ATP_WTA_ITF_Regex).nullable(),
  lastITFRankingYear: bestRankingYearSchema.nullable(),
});
export type UpdatePlayerITFRankingSchema = z.infer<typeof updatePlayerITFRankingSchema>;

export const updatePlayerPlayingStyleSchema = z.object({ playingStyle: z.enum(playerPlayingStyles, { message: requiredMsg }) });
export type UpdatePlayerPlayingStyleSchema = z.infer<typeof updatePlayerPlayingStyleSchema>;

export const updatePlayerLateralitySchema = z.object({ laterality: z.enum(playerLaterality, { message: requiredMsg }) });
export type UpdatePlayerLateralitySchema = z.infer<typeof updatePlayerLateralitySchema>;

export const updatePlayerPreferredSurfaceSchema = z.object({ preferredSurface: z.enum(playerPreferredSurface, { message: requiredMsg }) });
export type UpdatePlayerPreferredSurfaceSchema = z.infer<typeof updatePlayerPreferredSurfaceSchema>;

export const updatePlayerPreferredPriceSchema = z.object({ preferredPrice: z.coerce.number({ message: requiredMsg }).positive({ message: priceMsg }) });
export type UpdatePlayerPreferredPriceSchema = z.infer<typeof updatePlayerPreferredPriceSchema>;

export const updatePlayerSpecsSchema = updatePlayerPlayingStyleSchema
  .merge(updatePlayerLateralitySchema)
  .merge(updatePlayerPreferredSurfaceSchema)
  .merge(updatePlayerPreferredPriceSchema)
  .partial();
export type UpdatePlayerSpecsSchema = z.infer<typeof updatePlayerSpecsSchema>;

export const playerClubSchema = z.discriminatedUnion("clubCountry", [
  z.object({
    id: z.string().uuid().optional(),
    clubName: z.string().min(2, { message: minLengthMsg }),
    clubCountry: z.literal("fr"),
    clubDivision: z.enum(frDivisionsList, { message: requiredMsg }),
  }),
  z.object({
    id: z.string().uuid().optional(),
    clubName: z.string().min(2, { message: minLengthMsg }),
    clubCountry: z.literal("re"),
    clubDivision: z.enum(frDivisionsList, { message: requiredMsg }),
  }),
  z.object({
    id: z.string().uuid().optional(),
    clubName: z.string().min(2, { message: minLengthMsg }),
    clubCountry: z.literal("de"),
    clubDivision: z.enum(deDivisionsList, { message: requiredMsg }),
  }),
  z.object({
    id: z.string().uuid().optional(),
    clubName: z.string().min(2, { message: minLengthMsg }),
    clubCountry: z.literal("it"),
    clubDivision: z.enum(itDivisionsList, { message: requiredMsg }),
  }),
  z.object({
    id: z.string().uuid().optional(),
    clubName: z.string().min(2, { message: minLengthMsg }),
    clubCountry: z.enum(["be", "ch", "lu"]),
    clubDivision: z.string().min(2, { message: minLengthMsg }),
  }),
], { errorMap: () => ({ message: requiredMsg }) });

export type PlayerClubSchema = z.infer<typeof playerClubSchema>;

export const playerClubSchemaDelete = z.object({
  id: z.string().uuid(),
});

export const deletePlayerClubSchema = z.object({ id: z.string().uuid().optional() });
export type DeletePlayerClubSchema = z.infer<typeof deletePlayerClubSchema>;

export const updatePlayerClubSchema = z.object({ clubs: z.array(playerClubSchema) });
export type UpdatePlayerClubSchema = z.infer<typeof updatePlayerClubSchema>;

export const updatePlayerTopVictorySchema = z.object({
  topVictoryYear: bestRankingYearSchema.nullable(),
  topVictoryRankingType: z.enum(rankingTypes).nullable(),
  topVictoryRanking: z.union([z.enum(playerFFTranking), z.string().regex(ATP_WTA_ITF_Regex), z.string().regex(UTRRegex)]).nullable(),
  topVictoryRankingNumber: z.coerce.number().nullable(),
});
export type UpdatePlayerTopVictorySchema = z.infer<typeof updatePlayerTopVictorySchema>;

export const updatePlayerMatchAvailabilitiesSchema = z.object({
  matchAvailabilities: z.enum(playerMatchAvailabilities),
  matchAvailabilitiesCountries: z.array(z.enum(matchAndClubCountryCodes)),
});
export type UpdatePlayerMatchAvailabilitiesSchema = z.infer<typeof updatePlayerMatchAvailabilitiesSchema>;

export const updatePlayerFavoritesSchema = z.object({ clubId: z.string().uuid() });
export type UpdatePlayerFavoritesSchema = z.infer<typeof updatePlayerFavoritesSchema>;

export const updateTournamentAvailabilitySchema = z.object({
  tournamentAvailabilities: z.enum(playerMatchAvailabilities),
});
export type UpdateTournamentAvailabilitySchema = z.infer<typeof updateTournamentAvailabilitySchema>;
